import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Trash2, Plus, Search, Package } from 'lucide-react';
import {
  fetchInvoiceItems,
  addInvoiceItem,
  updateInvoiceItem,
  deleteInvoiceItem,
  updateSupplierInvoice,
  fetchItems,
  fetchBranches,
  fetchSuppliers,
  fetchSettings
} from '@/api';
import type { SupplierInvoice, Supplier } from '@/types/supplier';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const SKU_MAX_LENGTH = 8;
const AUTO_SKU_LENGTH = 5;
const SKU_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

const generateShortSku = () =>
  Array.from({ length: AUTO_SKU_LENGTH }, () =>
    SKU_CHARACTERS[Math.floor(Math.random() * SKU_CHARACTERS.length)]
  ).join('');

interface EditInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: SupplierInvoice | null;
  onUpdate: () => void;
  onReturnItem?: (invoice: SupplierInvoice) => void;
}

interface Item {
  id: string;
  name: string;
  sku: string;
  barcode?: string | null;
  buying_price: number;
  selling_price: number;
  quantity: number;
}

interface Branch {
  id: string;
  name: string;
}

interface InvoiceLineItem {
  id: string;
  item_id?: string;
  item_name: string;
  item_sku: string;
  barcode?: string;
  quantity: number;
  buy_price: number;
  sell_price: number;
  total: number;
  is_new: boolean;
  db_id?: string; // ID from database if editing existing
  vat_enabled?: boolean; // whether this line's total should include VAT when recalculated
}

export function EditInvoiceDialog({ open, onOpenChange, invoice, onUpdate, onReturnItem }: EditInvoiceDialogProps) {
  const currencySymbol = useCurrencySymbol();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  // Form state
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);
  const [applyInventoryAdjustments, setApplyInventoryAdjustments] = useState(false);
  const [applyPriceUpdates, setApplyPriceUpdates] = useState(false);
  const [vatRate, setVatRate] = useState(0);

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewItemForm, setShowNewItemForm] = useState(false);

  // New item form
  const [newItemName, setNewItemName] = useState('');
  const [newItemSku, setNewItemSku] = useState('');
  const [newItemQty, setNewItemQty] = useState(1);
  const [newItemBuyPrice, setNewItemBuyPrice] = useState(0);
  const [newItemSellPrice, setNewItemSellPrice] = useState(0);
  const [conflictSku, setConflictSku] = useState<string | null>(null);
  const [newItemBarcode, setNewItemBarcode] = useState('');
  const [conflictBarcode, setConflictBarcode] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  useEffect(() => {
    if (invoice && open) {
      setSupplierId(invoice.supplierId);
      setInvoiceNumber(invoice.invoiceNumber);
      setInvoiceDate(invoice.date);
      setDueDate(invoice.dueDate);
    }
  }, [invoice, open]);

  // Set default branch when branches are loaded
  useEffect(() => {
    if (branches.length > 0 && !selectedBranch) {
      const mainBranch = branches.find((b: any) => b.isMain);
      setSelectedBranch(mainBranch?.id || branches[0]?.id || '');
    }
  }, [branches]);

  // Load invoice items once items list is available
  useEffect(() => {
    if (invoice && open && items.length > 0) {
      loadInvoiceItems();
    }
  }, [invoice, open, items]);

  // Auto-calculate due date when supplier or invoice date changes
  useEffect(() => {
    if (supplierId && invoiceDate) {
      const supplier = suppliers.find(s => s.id === supplierId);
      if (supplier?.paymentTerm) {
        const invoiceDateObj = new Date(invoiceDate);
        let daysToAdd = 0;

        switch (supplier.paymentTerm) {
          case '15days':
            daysToAdd = 15;
            break;
          case '30days':
            daysToAdd = 30;
            break;
          case 'immediate':
          default:
            daysToAdd = 0;
        }

        invoiceDateObj.setDate(invoiceDateObj.getDate() + daysToAdd);
        const newDueDate = invoiceDateObj.toISOString().split('T')[0];
        setDueDate(newDueDate);
      }
    }
  }, [supplierId, invoiceDate, suppliers]);

  const loadData = async () => {
    try {
      const [itemsRes, branchesRes, suppliersRes, settingsRes] = await Promise.all([
        fetchItems(),
        fetchBranches(),
        fetchSuppliers(),
        fetchSettings()
      ]);
      // Parse numeric fields from API response
      const parsedItems = (itemsRes.data || []).map((item: any) => ({
        ...item,
        buying_price: parseFloat(item.buying_price) || 0,
        selling_price: parseFloat(item.selling_price) || 0,
        quantity: parseInt(item.quantity, 10) || 0
      }));
      setItems(parsedItems);
      setBranches(branchesRes.data || []);
      setSuppliers(suppliersRes.data || []);
      const rawVatRate = settingsRes?.data?.vat_percentage ?? settingsRes?.data?.vatPercentage ?? 0;
      setVatRate(Number(rawVatRate) || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
  };

  const computeLineTotal = (quantity: number, buyPrice: number, vatEnabled?: boolean) => {
    const subtotal = quantity * buyPrice;
    if (!vatEnabled || vatRate <= 0) return subtotal;
    return subtotal + (subtotal * vatRate) / 100;
  };

  const loadInvoiceItems = async () => {
    if (!invoice) return;

    try {
      setLoading(true);
      const response = await fetchInvoiceItems(invoice.id);
      const invoiceItems = response.data?.data || response.data || [];

      // Convert to line items format
      const converted: InvoiceLineItem[] = invoiceItems.map((item: any) => {
        // Try to get selling_price from item data or from items list
        let sellPrice = Number(item.selling_price) || 0;
        if (!sellPrice && item.item_id) {
          const matchedItem = items.find(i => i.id === item.item_id);
          if (matchedItem) {
            sellPrice = matchedItem.selling_price;
          }
        }

        const quantity = Number(item.quantity) || 0;
        const buyPrice = Number(item.unit_price) || 0;
        const total = Number(item.total_price) || 0;
        // supplier_invoice_items has no persisted per-line VAT flag, so infer
        // whether VAT was actually baked into this line's stored total by
        // comparing it to the raw (qty * unit_price) amount, rather than
        // trusting the item's current catalog vat_enabled flag (which may
        // have changed since this invoice was created).
        const vatEnabled = total - quantity * buyPrice > 0.01;

        return {
          id: `existing-${item.id}`,
          db_id: item.id,
          item_id: item.item_id,
          item_name: item.item_name || item.name || '',
          item_sku: item.item_sku || item.sku || '',
          barcode: item.barcode || '',
          quantity,
          buy_price: buyPrice,
          sell_price: sellPrice,
          total,
          is_new: false,
          vat_enabled: vatEnabled
        };
      });

      setLineItems(converted);
    } catch (error) {
      console.error('Failed to load invoice items:', error);
      toast.error('Failed to load invoice items');
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items.slice(0, 20);
    const term = searchTerm.toLowerCase();
    return items.filter(item =>
      item.name.toLowerCase().includes(term) ||
      item.sku.toLowerCase().includes(term)
    ).slice(0, 20);
  }, [items, searchTerm]);

  // Calculate totals
  const invoiceTotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + item.total, 0);
  }, [lineItems]);

  // Add existing item to line items
  const handleAddExistingItem = (item: Item) => {
    setConflictSku(null);
    setConflictBarcode(null);
    const existingIndex = lineItems.findIndex(li => li.item_id === item.id);
    if (existingIndex >= 0) {
      const updated = [...lineItems];
      updated[existingIndex].quantity += 1;
      updated[existingIndex].total = computeLineTotal(
        updated[existingIndex].quantity,
        updated[existingIndex].buy_price,
        updated[existingIndex].vat_enabled
      );
      setLineItems(updated);
    } else {
      const vatEnabled = Boolean((item as any).vat_enabled);
      const newLineItem: InvoiceLineItem = {
        id: `new-${Date.now()}`,
        item_id: item.id,
        item_name: item.name,
        item_sku: item.sku,
        barcode: item.barcode || '',
        quantity: 1,
        buy_price: item.buying_price,
        sell_price: item.selling_price,
        total: computeLineTotal(1, item.buying_price, vatEnabled),
        is_new: false,
        vat_enabled: vatEnabled
      };
      setLineItems([...lineItems, newLineItem]);
    }
    setSearchOpen(false);
    setSearchTerm('');
  };

  // Add new item
  const handleAddNewItem = () => {
    if (!newItemName.trim()) {
      toast.error('Please enter item name');
      return;
    }
    if (newItemSku.trim().length > SKU_MAX_LENGTH) {
      toast.error('SKU must be 8 characters or fewer');
      return;
    }

    const newLineItem: InvoiceLineItem = {
      id: `new-${Date.now()}`,
      item_name: newItemName,
      item_sku: newItemSku.trim() || generateShortSku(),
      barcode: newItemBarcode,
      quantity: newItemQty,
      buy_price: newItemBuyPrice,
      sell_price: newItemSellPrice,
      total: newItemQty * newItemBuyPrice,
      is_new: true
    };

    setLineItems([...lineItems, newLineItem]);
    setConflictSku(null);
    setConflictBarcode(null);

    // Reset form
    setNewItemName('');
    setNewItemSku('');
    setNewItemQty(1);
    setNewItemBuyPrice(0);
    setNewItemSellPrice(0);
    setNewItemBarcode('');
    setShowNewItemForm(false);
  };

  // Update line item
  const handleUpdateLineItem = (id: string, field: keyof InvoiceLineItem, value: any) => {
    if (field === 'item_sku') {
      setConflictSku(null);
    }
    if (field === 'barcode') {
      setConflictBarcode(null);
    }
    setLineItems(items =>
      items.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'buy_price') {
            updated.total = computeLineTotal(updated.quantity, updated.buy_price, updated.vat_enabled);
          }
          return updated;
        }
        return item;
      })
    );
  };

  // Remove line item
  const handleRemoveLineItem = (id: string) => {
    setLineItems(items => items.filter(item => item.id !== id));
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `${currencySymbol}${(num || 0).toFixed(2)}`;
  };

  const handleSubmit = async () => {
    if (!invoice) return;

    if (!supplierId || !invoiceNumber || !invoiceDate || !dueDate) {
      toast.error('Please fill all required fields');
      return;
    }

    if (lineItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    if (lineItems.some(item => !item.item_id && item.item_sku.trim().length > SKU_MAX_LENGTH)) {
      toast.error('SKU must be 8 characters or fewer');
      return;
    }
    if (applyInventoryAdjustments && !selectedBranch) {
      toast.error('Please select a branch for inventory adjustments');
      return;
    }

    setLoading(true);

    try {
      // Get original items FIRST to find deletions (before any updates)
      const originalResponse = await fetchInvoiceItems(invoice.id);
      const originalItems = originalResponse.data?.data || originalResponse.data || [];
      const existingDbIds = lineItems.filter(li => li.db_id).map(li => li.db_id);

      // Delete items that are no longer in the list
      for (const origItem of originalItems) {
        if (!existingDbIds.includes(origItem.id)) {
          console.log('Deleting item:', origItem.id);
          await deleteInvoiceItem(invoice.id, origItem.id, {
            adjust_inventory: applyInventoryAdjustments,
            branch_id: selectedBranch,
          });
        }
      }

      // Update existing items and add new ones
      for (const lineItem of lineItems) {
        const matchedCatalogItem = lineItem.item_id
          ? null
          : items.find((catalogItem) => (catalogItem.sku || '').trim().toLowerCase() === (lineItem.item_sku || '').trim().toLowerCase());
        const resolvedItemId = lineItem.item_id || matchedCatalogItem?.id;

        if (lineItem.db_id) {
          // Update existing item
          console.log('Updating item:', lineItem.db_id, lineItem);
          await updateInvoiceItem(invoice.id, lineItem.db_id, {
            item_id: resolvedItemId,
            item_name: lineItem.item_name,
            item_sku: lineItem.item_sku,
            barcode: lineItem.barcode || null,
            quantity: lineItem.quantity,
            unit_price: lineItem.buy_price,
            total_price: lineItem.total,
            sell_price: lineItem.sell_price,
            adjust_inventory: applyInventoryAdjustments,
            update_prices: applyPriceUpdates,
            branch_id: selectedBranch
          });
        } else {
          // Add new item
          console.log('Adding new item:', lineItem);
          await addInvoiceItem(invoice.id, {
            item_id: resolvedItemId,
            item_name: lineItem.item_name,
            item_sku: lineItem.item_sku,
            barcode: lineItem.barcode || null,
            quantity: lineItem.quantity,
            unit_price: lineItem.buy_price,
            total_price: lineItem.total,
            sell_price: lineItem.sell_price,
            is_new: resolvedItemId ? false : lineItem.is_new,
            adjust_inventory: applyInventoryAdjustments,
            update_prices: applyPriceUpdates,
            branch_id: selectedBranch
          });
        }
      }

      // Update invoice basic info AFTER items so total is correct
      const formData = new FormData();
      formData.append('supplier_id', supplierId);
      formData.append('invoice_number', invoiceNumber);
      formData.append('date', invoiceDate);
      formData.append('due_date', dueDate);
      formData.append('amount', invoiceTotal.toString());
      formData.append('status', invoice.status);

      await updateSupplierInvoice(invoice.id, formData);

      toast.success('Invoice updated successfully');
      onUpdate();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Failed to update invoice:', error);
      const apiError = error?.response?.data || {};
      if (apiError?.barcode) {
        setConflictBarcode(String(apiError.barcode).trim());
      }
      if (apiError?.sku) {
        setConflictSku(String(apiError.sku).trim());
      }
      const duplicateName = apiError?.existing_item || apiError?.existing_item_name || 'an existing item';
      if (apiError?.barcode) {
        toast.error(`${apiError.error || 'Barcode already exists'} (existing item: ${duplicateName})`);
      } else if (apiError?.sku) {
        toast.error(`${apiError.error || 'SKU already exists'} (existing item: ${duplicateName})`);
      } else {
        toast.error(apiError?.error || 'Failed to update invoice');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedSupplier = suppliers.find(s => s.id === supplierId);

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Edit Supplier Invoice
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {invoice.paidAmount > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <span>
                This invoice has a payment recorded, so items can only be added or increased here.
                To remove a line or reduce a quantity, use Return Item instead — it adjusts the
                supplier's balance correctly.
              </span>
              {onReturnItem && (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 border-amber-400 text-amber-800 hover:bg-amber-100"
                  onClick={() => {
                    onOpenChange(false);
                    onReturnItem(invoice);
                  }}
                >
                  Return Item
                </Button>
              )}
            </div>
          )}
          {/* Invoice Header Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Supplier *</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSupplier?.paymentTerm && (
                <p className="text-xs text-gray-500 mt-1">
                  Payment terms: {selectedSupplier.paymentTerm === 'immediate' ? 'Immediate' : selectedSupplier.paymentTerm}
                </p>
              )}
            </div>

            <div>
              <Label>Invoice Number *</Label>
              <Input
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="INV-001"
              />
            </div>

            <div>
              <Label>Branch (for new items)</Label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger>
                  <SelectValue placeholder="Select branch..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Used for inventory when adding new items</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Invoice Date *</Label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* Invoice Items Section */}
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Package className="h-5 w-5" />
                Invoice Items
              </h3>
              <Badge variant="outline" className="text-lg px-3 py-1">
                Total: {formatCurrency(invoiceTotal)}
              </Badge>
            </div>

            <div className="flex flex-col gap-2 mb-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={applyInventoryAdjustments}
                  onChange={(e) => setApplyInventoryAdjustments(e.target.checked)}
                />
                Adjust inventory for item changes (uses selected branch)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={applyPriceUpdates}
                  onChange={(e) => setApplyPriceUpdates(e.target.checked)}
                />
                Update item prices from invoice (buy/sell)
              </label>
            </div>

            {/* Add Item Controls */}
            <div className="flex gap-2 mb-4">
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1">
                    <Search className="h-4 w-4 mr-2" />
                    Search Existing Item
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search items by name, SKU..."
                      value={searchTerm}
                      onValueChange={setSearchTerm}
                    />
                    <CommandList>
                      <CommandEmpty>No items found.</CommandEmpty>
                      <CommandGroup heading="Items">
                        {filteredItems.map(item => (
                          <CommandItem
                            key={item.id}
                            value={item.id}
                            onSelect={() => handleAddExistingItem(item)}
                            className="cursor-pointer"
                          >
                            <div className="flex justify-between w-full">
                              <div>
                                <span className="font-medium">{item.name}</span>
                                <span className="text-gray-500 ml-2">({item.sku})</span>
                              </div>
                              <span className="text-gray-600">{formatCurrency(item.buying_price)}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Button
                variant="default"
                onClick={() => setShowNewItemForm(!showNewItemForm)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Item
              </Button>
            </div>

            {/* New Item Form */}
            {showNewItemForm && (
              <div className="mb-4 p-4 border rounded-lg bg-blue-50">
                <h4 className="font-medium mb-3">Add New Item</h4>
                <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
                  <div className="md:col-span-2">
                    <Label>Item Name *</Label>
                    <Input
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      placeholder="Enter item name"
                    />
                  </div>
                  <div>
                    <Label>SKU</Label>
                    <Input
                      value={newItemSku}
                      maxLength={SKU_MAX_LENGTH}
                      onChange={(e) => {
                        setNewItemSku(e.target.value);
                        setConflictSku(null);
                      }}
                      placeholder="Auto-generated"
                      className={conflictSku && newItemSku.trim() === conflictSku ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : ''}
                    />
                  </div>
                  <div>
                    <Label>Barcode</Label>
                    <Input
                      value={newItemBarcode}
                      onChange={(e) => {
                        setNewItemBarcode(e.target.value);
                        setConflictBarcode(null);
                      }}
                      placeholder="Optional"
                      className={conflictBarcode && newItemBarcode.trim() === conflictBarcode ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : ''}
                    />
                  </div>
                  <div>
                    <Label>Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newItemQty === 0 ? '' : newItemQty}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewItemQty(value === '' ? 0 : (parseInt(value, 10) || 1));
                      }}
                    />
                  </div>
                  <div>
                    <Label>Buy Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newItemBuyPrice === 0 ? '' : newItemBuyPrice}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewItemBuyPrice(value === '' ? 0 : (parseFloat(value) || 0));
                      }}
                    />
                  </div>
                  <div>
                    <Label>Sell Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newItemSellPrice === 0 ? '' : newItemSellPrice}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewItemSellPrice(value === '' ? 0 : (parseFloat(value) || 0));
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button onClick={handleAddNewItem} size="sm">
                    Add to Invoice
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowNewItemForm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Line Items Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="min-w-[220px]">Item</TableHead>
                    <TableHead className="w-32">SKU</TableHead>
                    <TableHead className="w-32">Barcode</TableHead>
                    <TableHead className="text-center w-20">Qty</TableHead>
                    <TableHead className="text-right w-28">Buy Price</TableHead>
                    <TableHead className="text-right w-28">Sell Price</TableHead>
                    <TableHead className="text-right w-28">Total</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No items added. Search for existing items or add new ones.
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Input
                            value={item.item_name}
                            onChange={(e) => handleUpdateLineItem(item.id, 'item_name', e.target.value)}
                            className="h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.item_sku}
                            maxLength={SKU_MAX_LENGTH}
                            onChange={(e) => handleUpdateLineItem(item.id, 'item_sku', e.target.value)}
                            className={`h-8 ${conflictSku && (item.item_sku || '').trim() === conflictSku ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : ''}`}
                            placeholder="SKU"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.barcode || ''}
                            onChange={(e) => handleUpdateLineItem(item.id, 'barcode', e.target.value)}
                            className={`h-8 ${conflictBarcode && (item.barcode || '').trim() === conflictBarcode ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : ''}`}
                            placeholder="Barcode"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity === 0 ? '' : item.quantity}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleUpdateLineItem(item.id, 'quantity', value === '' ? 0 : (parseInt(value, 10) || 1));
                            }}
                            className="h-8 w-16 text-center"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.buy_price === 0 ? '' : item.buy_price}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleUpdateLineItem(item.id, 'buy_price', value === '' ? 0 : (parseFloat(value) || 0));
                            }}
                            className="h-8 w-24 text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.sell_price === 0 ? '' : item.sell_price}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleUpdateLineItem(item.id, 'sell_price', value === '' ? 0 : (parseFloat(value) || 0));
                            }}
                            className="h-8 w-24 text-right"
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(item.total)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveLineItem(item.id)}
                            disabled={invoice.paidAmount > 0}
                            title={invoice.paidAmount > 0 ? "This invoice has a payment recorded — use Return Item to remove a line" : undefined}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
